/**
 * Axios Response Interceptor
 *
 * The API responses now return data in multiple formats across different endpoints.
 * Some endpoints wrap the actual response inside a `data` property, while others
 * return the response directly.
 *
 * To standardize data handling across the application, this interceptor ensures:
 * 1. If the response structure contains a nested `data` field, it extracts and assigns it to `response.data`.
 * 2. This simplifies API response consumption by eliminating unnecessary `.data.data` references.
 * 3. The full Axios response structure is preserved, ensuring `status`, `statusText`,
 *    `headers`, and other Axios metadata remain accessible.
 *
 * This approach ensures consistency when consuming API responses and avoids the need
 * for additional processing throughout the codebase.
 */

import axios from 'axios';
import { apiRouter, applicationJson } from '../constant';
import { getAuthToken, refreshAuthToken } from '../utils/authToken';

let hasRefreshInProgress = false;
let pendingRequestsQueue: Array<any> = [];

function onTokenRefreshed(newToken: string): void {
  pendingRequestsQueue.forEach((requestCallback: any) =>
    requestCallback(newToken),
  );
  pendingRequestsQueue = [];
}

function addPendingRequest(callback: any): void {
  pendingRequestsQueue.push(callback);
}

const axiosInstance = axios.create({
  baseURL: apiRouter.APIURL,
  headers: {
    'Content-Type': applicationJson,
  },
});

axiosInstance.interceptors.request.use(async function(config) {
  config.headers!.Authorization = `Bearer ${await getAuthToken()}`;
  return config;
});

axiosInstance.interceptors.response.use(
  response => {
    if (
      response.data &&
      typeof response.data === 'object' &&
      'data' in response.data
    ) {
      response.data = response.data.data;
    }

    return response;
  },
  async function(error) {
    const originalRequest = error.config;
    if (!originalRequest?._retry) {
      originalRequest._retry = false;
    }

    if (error?.response?.status === 401 && !originalRequest._retry) {
      if (hasRefreshInProgress) {
        return new Promise(resolve => {
          addPendingRequest((newToken: string) => {
            originalRequest._retry = true;
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            resolve(axiosInstance(originalRequest));
          });
        });
      }

      hasRefreshInProgress = true;
      originalRequest._retry = true;

      try {
        const newToken = await refreshAuthToken();
        axiosInstance.defaults.headers.common[
          'Authorization'
        ] = `Bearer ${newToken}`;
        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;

        onTokenRefreshed(newToken);
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        return Promise.reject(error);
      } finally {
        hasRefreshInProgress = false;
      }
    }
    return Promise.reject(error);
  },
);

export { axiosInstance };
